/**
 * Shared active-unit resolution (Jaime spec 2026-09-03, Usuarios directory).
 *
 * CANONICAL: this is the SAME rule menus/entitlements use. It covers owners
 * (activeOwnedUnitId) AND residents/familiar (unitId carried on the condo
 * context / membership rows of the /auth/me contexts payload), for both
 * standalone and condo-linked units. Consumers:
 *   - useEntitlements (unitId sent to /entitlements/me)
 *   - StaffDirectory (unit-scoped Usuarios view when the Hogar lens has an
 *     active unit of ANY member role; condo-wide directory only on the
 *     dedicated admin route /building/staff-directory).
 */
import type { AccountContext, CondoMembership } from '../contexts/AuthContext';

export const RESIDENT_CONTEXT_ROLES = ['resident', 'residente', 'propietario', 'familiar'];

export interface ResolveActiveUnitArgs {
  activeOwnedUnitId?: string | null;
  activeCondoId?: string | null;
  contexts?: AccountContext[];
  memberships?: CondoMembership[];
}

export function resolveActiveUnitId({ activeOwnedUnitId, activeCondoId, contexts, memberships }: ResolveActiveUnitArgs): string | undefined {
  // 1) Explicitly selected owned unit always wins (standalone OR condo-linked).
  if (activeOwnedUnitId) return String(activeOwnedUnitId);
  // 2) Condo lens (condo context active): admins stay condo-wide, but
  //    residents/familiar resolve their household unit — same rule as the
  //    Hogar lens (Jaime 2026-09-07 unified Usuarios ruling: one rule set for
  //    standalone AND condo-linked units). Without this, a resident whose
  //    active condo context carries a unitId got `undefined` and an empty
  //    Usuarios directory.
  if (activeCondoId) {
    const condoCtx = (contexts || []).find((c) => c.kind === 'condo' && c.condoId === activeCondoId && c.unitId && RESIDENT_CONTEXT_ROLES.includes(c.role || ''));
    if (condoCtx?.unitId) return String(condoCtx.unitId);
    const condoMembership = (memberships || []).find((m) => m.condoId === activeCondoId && m.unitId && RESIDENT_CONTEXT_ROLES.includes(m.role || ''));
    if (condoMembership?.unitId) return String(condoMembership.unitId);
    return undefined;
  }
  // 3) Hogar lens without an owned-unit selection: resolve the resident
  //    membership's unitId from the /auth/me contexts payload (resident,
  //    propietario or familiar roles; standalone AND condo-linked).
  const ctx = (contexts || []).find((c) => c.kind === 'condo' && c.unitId && (!c.role || RESIDENT_CONTEXT_ROLES.includes(c.role)));
  const membership = (memberships || []).find((m) => m.unitId && (!m.role || RESIDENT_CONTEXT_ROLES.includes(m.role)));
  const fallbackId = ctx?.unitId || membership?.unitId;
  return fallbackId ? String(fallbackId) : undefined;
}
